import React from 'react';
import { View, StyleSheet, Platform, TouchableOpacity, useWindowDimensions, ScrollView } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { colors } from '../styles/colors';
import { Typography, SectionHeader } from './ui';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { typographyTokens } from '../styles/typographyTokens';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../styles/accentText';
interface PracticeCardProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
  tags: string[];
  badge: string;
  isHighlighted?: boolean;
  isDark?: boolean;
}

const PracticeCard: React.FC<PracticeCardProps> = ({ icon, title, description, tags, badge, isHighlighted, isDark }) => {
  const { isDark: isThemeDark, colors: themeColors } = useTheme();
  return (
    <TouchableOpacity
      style={[
        styles.practiceCard,
        !isHighlighted && { backgroundColor: themeColors.surfacePrimary },
        isHighlighted && styles.practiceCardHighlighted,
      ]}
      className={Platform.OS === 'web' ? (isHighlighted ? 'convex-glass-fire' : 'convex-glass-light') : ''}
    >
      <View style={styles.practiceCardContent}>
        <View style={styles.practiceCardHeader}>
          <View style={[
            styles.practiceIcon,
            !isHighlighted && !isDark && isThemeDark && { backgroundColor: 'rgba(237, 116, 61, 0.15)' },
            isHighlighted && styles.practiceIconHighlighted,
            isDark && styles.practiceIconDark,
          ]}>
            <Ionicons name={icon} size={20} color={isHighlighted ? ON_ACCENT_TEXT : isDark ? "#FFFFFF" : ACCENT_TEXT} />
          </View>
          <View style={[
            styles.practiceBadge,
            !isHighlighted && !isDark && isThemeDark && { backgroundColor: 'rgba(255,255,255,0.08)' },
            isHighlighted && styles.practiceBadgeHighlighted,
            isDark && styles.practiceBadgeDark,
          ]}>
            <Typography variant="captionAccent" style={[
              styles.practiceBadgeText,
              !isHighlighted && !isDark && { color: themeColors.textPrimary },
              isHighlighted && styles.practiceBadgeTextHighlighted,
              isDark && !isHighlighted && styles.practiceBadgeTextDark,
            ]}>{badge}</Typography>
          </View>
        </View>
        <Typography variant="h3" style={[styles.practiceTitle, isHighlighted && styles.practiceTitleHighlighted]}>{title}</Typography>
        <Typography variant="body" style={[styles.practiceDescription, isHighlighted && styles.practiceDescriptionHighlighted]}>{description}</Typography>
        <View style={styles.practiceTags}>
          {tags.map((tag, index) => (
            <View key={index} style={[
              styles.practiceTag,
              !isHighlighted && isThemeDark && { backgroundColor: 'rgba(255,255,255,0.06)' },
              isHighlighted && (index === 0 ? styles.practiceTagHighlightedPrimary : styles.practiceTagHighlighted),
            ]}>
              <Typography variant="captionAccent" style={[
                styles.practiceTagText,
                !isHighlighted && { color: themeColors.textPrimary },
                isHighlighted && (index === 0 ? styles.practiceTagTextHighlightedPrimary : styles.practiceTagTextHighlighted),
              ]}>{tag}</Typography>
            </View>
          ))}
        </View>
      </View>
    </TouchableOpacity>
  );
};

export const Practices: React.FC = () => {
  const { isDark, colors: themeColors } = useTheme();
  const { t } = useI18n('home');
  const { width } = useWindowDimensions();
  const isLargeScreen = width >= 1024;
  const isMediumScreen = width >= 768;

  const practices: PracticeCardProps[] = [
    {
      icon: 'body-outline',
      title: t('practices.meditative'),
      description: t('practices.meditativeDesc'),
      tags: [t('practices.meditativeTag1'), t('practices.meditativeTag2')],
      badge: t('practices.meditativeBadge'),
    },
    {
      icon: 'flash-outline',
      title: t('practices.neurofocus'),
      description: t('practices.neurofocusDesc'),
      tags: [t('practices.neurofocusTag1'), t('practices.neurofocusTag2')],
      badge: t('practices.neurofocusBadge'),
      isHighlighted: true,
    },
    {
      icon: 'sunny-outline',
      title: t('practices.stateDesign'),
      description: t('practices.stateDesignDesc'),
      tags: [t('practices.stateDesignTag')],
      badge: t('practices.stateDesignBadge'),
    },
    {
      icon: 'cloud-outline',
      title: t('practices.somaticBreathwork'),
      description: t('practices.somaticBreathworkDesc'),
      tags: [t('practices.somaticBreathworkTag1'), t('practices.somaticBreathworkTag2')],
      badge: t('practices.somaticBreathworkBadge'),
    },
    {
      icon: 'moon-outline',
      title: t('practices.sleepNonDoing'),
      description: t('practices.sleepNonDoingDesc'),
      tags: [t('practices.sleepNonDoingTag1'), t('practices.sleepNonDoingTag2')],
      badge: t('practices.sleepNonDoingBadge'),
      isDark: true,
    },
    {
      icon: 'people-outline',
      title: t('practices.communityCircles'),
      description: t('practices.communityCirclesDesc'),
      tags: [t('practices.communityCirclesTag')],
      badge: t('practices.communityCirclesBadge'),
    },
  ];

  const categories = [t('practices.allPractices'), t('practices.meditation'), t('practices.breathwork'), t('practices.movement')];

  return (
    <View style={[styles.section, { backgroundColor: themeColors.canvas }]}>
      <View style={styles.container}>
        <SectionHeader
          title={t('practices.title')}
          subtitle={t('practices.subtitle')}
          actionButton={
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScroll}>
              <View style={styles.categories}>
                {categories.map((category, index) => (
                  <TouchableOpacity
                    key={index}
                    style={[styles.categoryButton, index !== 0 && { backgroundColor: themeColors.surfacePrimary }, index === 0 && styles.categoryButtonActive]}
                    className={Platform.OS === 'web' ? (index === 0 ? 'convex-glass-fire' : 'convex-glass-light') : ''}
                  >
                    {index === 0 && <View style={styles.categoryDot} />}
                    <Typography variant="label" style={[styles.categoryText, index === 0 && styles.categoryTextActive]}>{category}</Typography>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          }
        />

        <View style={[
          styles.grid,
          isMediumScreen && styles.gridMedium,
          isLargeScreen && styles.gridLarge,
        ]}>
          {practices.map((practice, index) => (
            <PracticeCard key={index} {...practice} />
          ))}
        </View>

        <View style={styles.footer}>
          <TouchableOpacity
            style={[styles.exploreButton, { backgroundColor: themeColors.charcoal }]}
            className={Platform.OS === 'web' ? 'convex-glass-fire' : ''}
          >
            <Typography variant="button" style={styles.exploreButtonText}>{t('practices.exploreLibrary')}</Typography>
            <Ionicons name="arrow-forward" size={18} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    paddingVertical: 120,
  },
  container: {
    maxWidth: 1440,
    marginHorizontal: 'auto',
    paddingHorizontal: 32,
    width: '100%',
  },
  header: {
    marginBottom: 64,
    gap: 32,
  },
  headerLarge: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  headerTitle: {
    marginBottom: 16,
  },
  headerDescBox: {
    maxWidth: 600,
  },
  headerDesc: {
    opacity: 0.6,
  },
  categoryScroll: {
    overflow: 'visible',
  } as any,
  categories: {
    flexDirection: 'row',
    gap: 12,
    paddingBottom: 8,
  },
  categoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: 'white',
  },
  categoryButtonActive: {
    backgroundColor: '#E98245',
  },
  categoryDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'white',
  },
  categoryText: {
    ...typographyTokens.label,
    color: colors.charcoal,
    opacity: 0.6,
  },
  categoryTextActive: {
    color: ON_ACCENT_TEXT,
    opacity: 1,
  },
  grid: {
    gap: 32,
    marginBottom: 80,
  },
  gridMedium: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  gridLarge: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  practiceCard: {
    flex: 1,
    minWidth: Platform.OS === 'web' ? 'calc(33.333% - 22px)' : '100%',
    maxWidth: Platform.OS === 'web' ? 'calc(33.333% - 22px)' : '100%',
    padding: 40,
    borderRadius: 32,
    backgroundColor: 'white',
    position: 'relative',
    overflow: 'hidden',
  } as any,
  practiceCardHighlighted: {
    backgroundColor: '#E98245',
  },
  practiceCardContent: {
    position: 'relative',
    zIndex: 10,
  },
  practiceCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 32,
  },
  practiceIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: 'rgba(237, 116, 61, 0.05)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  practiceIconHighlighted: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  practiceIconDark: {
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  practiceBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: 'rgba(15, 20, 25, 0.04)',
  },
  practiceBadgeHighlighted: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
  },
  practiceBadgeDark: {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  practiceBadgeText: {
    ...typographyTokens.captionAccent,
    color: colors.charcoal,
    opacity: 0.5,
  },
  // CORE-1733 (возврат QA 23.10) — подпись на оранжевой карточке тёмная: белый .8 давал ~2.3:1.
  practiceBadgeTextHighlighted: {
    color: ON_ACCENT_TEXT,
  },
  practiceBadgeTextDark: {
    color: 'white',
    opacity: 0.8,
  },
  practiceTitle: {
    marginBottom: 12,
  },
  practiceTitleHighlighted: {
    color: ON_ACCENT_TEXT,
  },
  practiceDescription: {
    marginBottom: 32,
    opacity: 0.6,
  },
  practiceDescriptionHighlighted: {
    color: ON_ACCENT_TEXT,
    opacity: 0.8,
  },
  practiceTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  practiceTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: 'rgba(15, 20, 25, 0.03)',
  },
  practiceTagHighlighted: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  practiceTagHighlightedPrimary: {
    backgroundColor: 'white',
  },
  practiceTagText: {
    ...typographyTokens.captionAccent,
    color: colors.charcoal,
    opacity: 0.4,
  },
  practiceTagTextHighlighted: {
    color: 'white',
    opacity: 0.6,
  },
  practiceTagTextHighlightedPrimary: {
    color: ACCENT_TEXT,
    opacity: 1,
  },
  footer: {
    alignItems: 'center',
  },
  exploreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 40,
    paddingVertical: 20,
    borderRadius: 8,
    backgroundColor: colors.charcoal,
  },
  exploreButtonText: {
    color: 'white',
  },
});
